import React, { useCallback, useEffect, useState } from "react";
import { FaCheck, FaEye, FaEyeSlash, FaMicrosoft, FaSave, FaTrashAlt } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import "./SsoSettings.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const ROLES = [
  { value: "analyst",      label: "Analyst" },
  { value: "contributor",  label: "Contributor" },
  { value: "user_admin",   label: "User Administrator" },
];

const EMPTY_FORM = {
  tenantId:      "",
  clientId:      "",
  clientSecret:  "",
  redirectUri:   "",
  enabled:       false,
  autoProvision: true,
  defaultRole:   "analyst",
};

export default function SsoSettings() {
  const { token } = useAuth();

  const [form, setForm]           = useState(EMPTY_FORM);
  const [hasSecret, setHasSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [isNew, setIsNew]         = useState(true);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [banner, setBanner]       = useState(null); // { type: 'success'|'error', msg }

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/sso`, { headers: headers() });
      const data = await res.json();
      if (data.config) {
        const c = data.config;
        setForm({
          tenantId:      c.tenantId      || "",
          clientId:      c.clientId      || "",
          clientSecret:  "",               // never pre-fill secret from server
          redirectUri:   c.redirectUri   || "",
          enabled:       c.enabled       || false,
          autoProvision: c.autoProvision !== false,
          defaultRole:   c.defaultRole   || "analyst",
        });
        setHasSecret(c.hasClientSecret || false);
        setIsNew(false);
      } else {
        setForm(EMPTY_FORM);
        setHasSecret(false);
        setIsNew(true);
      }
    } catch {
      setBanner({ type: "error", msg: "Failed to load SSO configuration." });
    }
  }, [headers]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const set = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBanner(null);
    setSaving(true);
    try {
      const body = { ...form };
      // Don't send empty secret if the record already has one
      if (!body.clientSecret && hasSecret) delete body.clientSecret;

      const res = await fetch(`${API_BASE}/api/admin/sso`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setBanner({ type: "error", msg: data.message || "Failed to save." });
      } else {
        setBanner({ type: "success", msg: "SSO configuration saved." });
        await loadConfig();
      }
    } catch {
      setBanner({ type: "error", msg: "Network error — could not save." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove SSO configuration? Users will no longer be able to sign in via Microsoft.")) return;
    setBanner(null);
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/sso`, {
        method: "DELETE",
        headers: headers(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setBanner({ type: "error", msg: data.message || "Failed to remove." });
      } else {
        setBanner({ type: "success", msg: "SSO configuration removed." });
        setForm(EMPTY_FORM);
        setHasSecret(false);
        setIsNew(true);
      }
    } catch {
      setBanner({ type: "error", msg: "Network error — could not remove." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sso-page">
      {/* Header */}
      <div className="sso-header">
        <div className="sso-header-icon"><FaMicrosoft /></div>
        <div>
          <h2 className="sso-header-title">Azure Entra ID — Single Sign-On</h2>
          <p className="sso-header-desc">
            Allow users to authenticate with their Microsoft / Entra ID identity.
            Register an application in Azure, then paste the credentials below.
          </p>
        </div>
      </div>

      {/* Setup guide */}
      <div className="sso-guide">
        <p className="sso-guide-title">Azure App Registration — required settings</p>
        <ol className="sso-guide-list">
          <li>In <strong>Azure Portal → Entra ID → App registrations</strong>, create a new registration.</li>
          <li>Under <strong>Authentication</strong>, add a <em>Web</em> redirect URI matching the Redirect URI below.</li>
          <li>Under <strong>Certificates &amp; secrets</strong>, create a client secret and copy it here.</li>
          <li>Copy the <strong>Directory (tenant) ID</strong> and <strong>Application (client) ID</strong> from the Overview page.</li>
          <li>Grant the <strong>openid, email, profile</strong> API permissions (delegated).</li>
        </ol>
      </div>

      {banner && (
        <div className={`sso-banner sso-banner--${banner.type}`}>
          {banner.type === "success" && <FaCheck style={{ marginRight: 7 }} />}
          {banner.msg}
        </div>
      )}

      <form className="sso-form" onSubmit={handleSave}>
        {/* Enable toggle */}
        <div className="sso-toggle-row">
          <label className="sso-toggle-label" htmlFor="sso-enabled">
            <span className="sso-toggle-text">
              <span className="sso-toggle-title">Enable SSO</span>
              <span className="sso-toggle-sub">Show the "Sign in with SSO" button on the login page.</span>
            </span>
            <span className={`sso-toggle-pill ${form.enabled ? "on" : "off"}`}>
              <input
                id="sso-enabled"
                type="checkbox"
                checked={form.enabled}
                onChange={set("enabled")}
              />
              <span className="sso-pill-thumb" />
            </span>
          </label>
        </div>

        {/* Credentials */}
        <div className="sso-section">
          <p className="sso-section-title">App Registration Credentials</p>
          <div className="sso-fields">
            <label className="sso-field">
              <span className="sso-field-label">Directory (Tenant) ID <span className="sso-required">*</span></span>
              <input
                className="sso-input"
                type="text"
                value={form.tenantId}
                onChange={set("tenantId")}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
              />
            </label>

            <label className="sso-field">
              <span className="sso-field-label">Application (Client) ID <span className="sso-required">*</span></span>
              <input
                className="sso-input"
                type="text"
                value={form.clientId}
                onChange={set("clientId")}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
              />
            </label>

            <label className="sso-field">
              <span className="sso-field-label">
                Client Secret {!isNew && hasSecret && <span className="sso-stored">(stored — leave blank to keep)</span>}
                {isNew && <span className="sso-required">*</span>}
              </span>
              <div className="sso-secret-wrap">
                <input
                  className="sso-input sso-input--secret"
                  type={showSecret ? "text" : "password"}
                  value={form.clientSecret}
                  onChange={set("clientSecret")}
                  placeholder={hasSecret ? "Leave blank to keep existing secret" : "Paste client secret value"}
                  required={isNew}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="sso-secret-toggle"
                  onClick={() => setShowSecret((v) => !v)}
                  aria-label={showSecret ? "Hide secret" : "Show secret"}
                >
                  {showSecret ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            <label className="sso-field">
              <span className="sso-field-label">Redirect URI <span className="sso-hint">(leave blank for auto-detect)</span></span>
              <input
                className="sso-input"
                type="url"
                value={form.redirectUri}
                onChange={set("redirectUri")}
                placeholder="https://your-server/api/auth/sso/callback"
              />
              <span className="sso-field-help">
                Must exactly match the redirect URI registered in Azure.
                Auto-detect works when the server is behind a single domain.
              </span>
            </label>
          </div>
        </div>

        {/* Provisioning */}
        <div className="sso-section">
          <p className="sso-section-title">User Provisioning</p>
          <div className="sso-fields">
            <div className="sso-toggle-row sso-toggle-row--inline">
              <label className="sso-toggle-label" htmlFor="sso-autoprovision">
                <span className="sso-toggle-text">
                  <span className="sso-toggle-title">Auto-provision new users</span>
                  <span className="sso-toggle-sub">
                    Automatically create an account for first-time SSO users.
                    If off, only pre-existing accounts can sign in via SSO.
                  </span>
                </span>
                <span className={`sso-toggle-pill ${form.autoProvision ? "on" : "off"}`}>
                  <input
                    id="sso-autoprovision"
                    type="checkbox"
                    checked={form.autoProvision}
                    onChange={set("autoProvision")}
                  />
                  <span className="sso-pill-thumb" />
                </span>
              </label>
            </div>

            {form.autoProvision && (
              <label className="sso-field">
                <span className="sso-field-label">Default role for auto-provisioned users</span>
                <select className="sso-select" value={form.defaultRole} onChange={set("defaultRole")}>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="sso-actions">
          <button className="sso-btn sso-btn--save" type="submit" disabled={saving}>
            <FaSave />
            {saving ? "Saving…" : "Save Configuration"}
          </button>
          {!isNew && (
            <button
              className="sso-btn sso-btn--delete"
              type="button"
              onClick={handleDelete}
              disabled={deleting}
            >
              <FaTrashAlt />
              {deleting ? "Removing…" : "Remove SSO"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
